/**
 * A refusal from the AI surface, rendered as the state it actually is.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THE BUDGET STATE IS THE REASON THIS COMPONENT EXISTS
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `states/ApiErrorNote.tsx` would render `429 WORKFORCE_QUOTA_EXCEEDED` correctly by
 * its own rules — the server's sentence, `severity: medium`, and a `RetryAction`,
 * because `isRetryable` says yes to every `429`. That is right for the generic note
 * and wrong here, and the wrongness is specific: **a retry button under a spent
 * budget invites somebody to press a control whose every press is refused before the
 * request is sent.**
 *
 * Three sentences that must never be merged, and this component keeps them apart:
 *
 *   *"Nothing was found."*         → `EmptyState`, elsewhere on the screen, no
 *                                    severity, `inbox`, naming the **filter**.
 *   *"The model could not be
 *     reached."*                   → `unavailable`, `triangle-alert`. Normally not
 *                                    even an error — a chat turn answers `200` with
 *                                    the arithmetic fallback — so reaching it means
 *                                    the fallback path did not cover this.
 *   *"We chose not to spend."*     → `budget`, `ban`, and it says out loud that it is
 *                                    neither of the other two.
 *
 * Its near neighbour is `429 WORKFORCE_RATE_LIMITED`: the same status code, the
 * opposite advice. `hourglass` against `ban`, `medium` against `high`, a retry
 * against no retry — the same two metaphors `FreshnessNote` uses to keep `scheduled`
 * apart from `requires_request`, because it is the same distinction: something is
 * coming, or nothing is.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THE SERVER'S OWN SENTENCE IS RENDERED, NOT REPLACED
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * Task 45 shipped a commit fixing 27 swallowed refusals. Every branch here prints
 * `serverSentence` when §5.1 marks the code safe, and says *why* it is absent when it
 * does not — never silently dropping it, and never substituting a sentence of this
 * client's own for one the server wrote.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  AND THE THREE NUMBERS ARE NEVER ZERO
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `period_month`, `cost_usd` and `limit_usd` come off `details`. A `429` that omitted
 * one renders a sentence saying it was not published — never `0`, which on a spend
 * figure would be a lie about the company in the direction that makes the refusal
 * look like a bug.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { AiRefusal } from '../../ai/refusal';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { PermissionDenied } from '../../states/PermissionDenied';
import { RetryAction } from '../../states/RetryAction';
import { StatusNote } from '../../states/StatusNote';
import { Badge } from '../../states/Badge';
import type { WorkforceApiError } from '../../api/errors';
import { ErrorCodeLine } from '../../states/ErrorCodeLine';

function ServerSentence({
  sentence,
  code,
  correlationId,
  testId,
}: {
  readonly sentence: string | null;
  readonly code: string;
  readonly correlationId: string | null;
  readonly testId: string;
}): ReactNode {
  const { t } = useI18n();
  return (
    <>
      {sentence === null ? (
        <p className="wp-small" data-testid={`${testId}-withheld`}>
          {t('m4.refusal.serverSentence.withheld')}
        </p>
      ) : (
        <p data-testid={testId}>
          <span className="wp-small wp-muted">{t('m4.refusal.serverSentence')} </span>
          {sentence}
        </p>
      )}
      <ErrorCodeLine code={code} correlationId={correlationId} />
    </>
  );
}

/** One spend figure, or the sentence that says it was not published. Never a zero. */
function Money({
  labelKey,
  value,
  testId,
}: {
  readonly labelKey:
    'm4.refusal.budget.period' | 'm4.refusal.budget.cost' | 'm4.refusal.budget.limit';
  readonly value: string | null;
  readonly testId: string;
}): ReactNode {
  const { t } = useI18n();
  return (
    <p className="wp-small">
      {t(labelKey)}:{' '}
      {value === null ? (
        <span data-testid={`${testId}-unpublished`}>{t('m4.refusal.budget.unpublished')}</span>
      ) : (
        <span className="wp-mono" data-testid={testId}>
          {value}
        </span>
      )}
    </p>
  );
}

export interface RefusalPanelProps {
  readonly refusal: AiRefusal;
  /** The original error, for the two branches the shared components handle better. */
  readonly error: WorkforceApiError;
  readonly subject: string;
  readonly onRetry?: () => void;
}

export function RefusalPanel({ refusal, error, subject, onRetry }: RefusalPanelProps): ReactNode {
  const { t } = useI18n();

  if (refusal.kind === 'denied') {
    return <PermissionDenied correlationId={refusal.correlationId} testId="m4-refusal-denied" />;
  }

  if (refusal.kind === 'budget') {
    return (
      <StatusNote
        severity="high"
        role="alert"
        title={t('m4.refusal.budget.title')}
        testId="m4-refusal-budget"
      >
        {/* The prohibition sign, not an alarm: nothing is broken and nothing is
            coming. Same silhouette pairing as FreshnessNote's `requires_request`. */}
        <p className="wp-flags">
          <Badge
            severity="high"
            icon="ban"
            label={t('m4.status.budget')}
            testId="m4-refusal-budget-tag"
          />
        </p>
        <p>{t('m4.refusal.budget.body')}</p>
        {/* Separate test ids so no assertion here can be satisfied by a neighbouring
            element — the trap that has caught three UI tasks on this build. */}
        <p data-testid="m4-refusal-budget-not-outage">{t('m4.refusal.budget.notOutage')}</p>
        <p data-testid="m4-refusal-budget-not-empty">{t('m4.refusal.budget.notEmpty')}</p>
        <Money
          labelKey="m4.refusal.budget.period"
          value={refusal.periodMonth}
          testId="m4-refusal-budget-period"
        />
        <Money
          labelKey="m4.refusal.budget.cost"
          value={refusal.costUsd}
          testId="m4-refusal-budget-cost"
        />
        <Money
          labelKey="m4.refusal.budget.limit"
          value={refusal.limitUsd}
          testId="m4-refusal-budget-limit"
        />
        <ServerSentence
          sentence={refusal.serverSentence}
          code={refusal.code}
          correlationId={refusal.correlationId}
          testId="m4-refusal-budget-sentence"
        />
        {/* No retry control, and specifically no greyed one. `onRetry` is not even
            read on this branch, which is why `AiRefusal`'s budget variant carries no
            `retryAfterSeconds`: there is nothing in the model to wire one from. */}
        <p className="wp-small" data-testid="m4-refusal-budget-no-retry">
          {t('m4.refusal.budget.noRetry')}
        </p>
        <p className="wp-small wp-muted" data-testid="m4-refusal-budget-no-endpoint">
          {t('m4.refusal.budget.noEndpoint')}
        </p>
      </StatusNote>
    );
  }

  if (refusal.kind === 'busy') {
    return (
      <StatusNote
        severity="medium"
        title={t('m4.refusal.busy.title')}
        testId="m4-refusal-busy"
        {...(onRetry === undefined
          ? {}
          : {
              actions: (
                <RetryAction
                  onRetry={onRetry}
                  retryAfterSeconds={refusal.retryAfterSeconds}
                  testId="m4-refusal-busy-retry"
                />
              ),
            })}
      >
        {/* A clock: something is coming. The opposite metaphor to the budget note's
            prohibition sign, and two silhouettes a reader tells apart with the colour
            gone — which is what UI_PROCESS §4's greyscale screenshot actually tests. */}
        <p className="wp-flags">
          <Badge
            severity="medium"
            icon="hourglass"
            label={t('m4.refusal.busy.title')}
            testId="m4-refusal-busy-tag"
          />
        </p>
        <p>{t('m4.refusal.busy.body')}</p>
        <p data-testid="m4-refusal-busy-wait">{t('m4.refusal.busy.wait')}</p>
        <p className="wp-small" data-testid="m4-refusal-busy-slots">
          {refusal.inFlight === null || refusal.limit === null
            ? t('m4.refusal.busy.slotsUnknown')
            : t('m4.refusal.busy.slots', { inFlight: refusal.inFlight, limit: refusal.limit })}
        </p>
        <ServerSentence
          sentence={refusal.serverSentence}
          code={refusal.code}
          correlationId={refusal.correlationId}
          testId="m4-refusal-busy-sentence"
        />
      </StatusNote>
    );
  }

  if (refusal.kind === 'unavailable') {
    return (
      <StatusNote
        severity="high"
        role="alert"
        title={t('m4.refusal.unavailable.title')}
        testId="m4-refusal-unavailable"
        {...(onRetry === undefined
          ? {}
          : { actions: <RetryAction onRetry={onRetry} testId="m4-refusal-unavailable-retry" /> })}
      >
        <p>{t('m4.refusal.unavailable.body')}</p>
        <ServerSentence
          sentence={refusal.serverSentence}
          code={refusal.code}
          correlationId={refusal.correlationId}
          testId="m4-refusal-unavailable-sentence"
        />
      </StatusNote>
    );
  }

  // Everything else goes to the shared note rather than to a copy of it here: the
  // §5.1 decision about which messages may be shown belongs in one place.
  return (
    <ApiErrorNote
      error={error}
      subject={subject}
      testId="m4-refusal-other"
      {...(onRetry === undefined ? {} : { onRetry })}
    />
  );
}
